import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AumBreadcrumbComponentItem } from '@modeso/aumico-lib-core-fe';
import { HeaderCrumb, HeaderSections, HeaderTab, HeaderTabId } from './workspace-header.types';

const TAB_LABEL_KEYS: Record<HeaderTabId, string> = {
    accounts: 'aumico_abcp_header_tab_accounts',
    documents: 'aumico_abcp_header_tab_documents',
    adjustments: 'aumico_abcp_header_tab_adjustments',
    openItems: 'aumico_abcp_header_tab_open_items',
    comments: 'aumico_abcp_header_tab_comments',
};

const SEARCH_PLACEHOLDER_KEYS: Record<HeaderTabId, string> = {
    accounts: 'aumico_abcp_header_search_accounts',
    documents: 'aumico_abcp_header_search_documents',
    adjustments: 'aumico_abcp_header_search_adjustments',
    openItems: 'aumico_abcp_header_search_open_items',
    comments: 'aumico_abcp_header_search_comments',
};

@Component({
    selector: 'abcp-workspace-header',
    templateUrl: './workspace-header.component.html',
    styleUrls: ['./workspace-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpWorkspaceHeaderComponent implements OnChanges {
    @Input() sections: HeaderSections = {};
    @Input() loading = false;
    @Input() error = false;

    @Output() crumbSelect = new EventEmitter<string>();
    @Output() erpRefresh = new EventEmitter<void>();
    @Output() tabSelect = new EventEmitter<HeaderTabId>();
    @Output() searchChange = new EventEmitter<string>();

    crumbItems: AumBreadcrumbComponentItem[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['sections']) {
            this.crumbItems = this.buildCrumbs(this.sections.path ?? []);
        }
    }

    get wantsPath(): boolean {
        return this.sections.path !== undefined;
    }

    get wantsTabs(): boolean {
        return this.sections.tabs !== undefined;
    }

    get hasPath(): boolean {
        return (this.sections.path?.length ?? 0) > 0;
    }

    get back(): HeaderCrumb | null {
        return this.sections.back ?? null;
    }

    get hasActions(): boolean {
        return this.sections.actions !== undefined;
    }

    get hasTabs(): boolean {
        return (this.sections.tabs?.items?.length ?? 0) > 0;
    }

    get isEmpty(): boolean {
        return !this.hasPath && !this.hasActions && !this.hasTabs;
    }

    get tabItems(): HeaderTab[] {
        return this.sections.tabs?.items ?? [];
    }

    get activeTabId(): HeaderTabId | null {
        return this.sections.tabs?.activeId ?? null;
    }

    get lastSync(): string | Date | null {
        return this.sections.actions?.lastSync ?? null;
    }

    /** The connected vendor, for the logo on the button. Null when nothing is connected. */
    get erpVendor(): { logoSrc: string; name: string } | null {
        return this.sections.actions?.erpVendor ?? null;
    }

    get searchTabs(): HeaderTabId[] {
        return this.activeTabId ? [this.activeTabId] : [];
    }

    hasCount(tab: HeaderTab): boolean {
        return tab.count !== null && tab.count !== undefined;
    }

    tabLabelKey(id: HeaderTabId): string {
        return TAB_LABEL_KEYS[id];
    }

    searchPlaceholderKey(id: HeaderTabId): string {
        return SEARCH_PLACEHOLDER_KEYS[id];
    }

    trackByTabId(_index: number, tab: HeaderTab): HeaderTabId {
        return tab.id;
    }

    trackBySelf(_index: number, id: HeaderTabId): HeaderTabId {
        return id;
    }

    onBackClick(id: string): void {
        this.crumbSelect.emit(id);
    }

    onErpClick(): void {
        this.erpRefresh.emit();
    }

    onTabClick(id: HeaderTabId): void {
        this.tabSelect.emit(id);
    }

    onSearch(value: string | null): void {
        this.searchChange.emit(value ?? '');
    }

    private buildCrumbs(path: HeaderCrumb[]): AumBreadcrumbComponentItem[] {
        return path.map((crumb, index) => {
            const isLast = index === path.length - 1;
            const item: AumBreadcrumbComponentItem = { displayName: crumb.label, translated: true, isActive: isLast };
            if (!isLast) {
                item.onClick = () => this.crumbSelect.emit(crumb.id);
            }
            return item;
        });
    }
}
